import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, CircleMarker, Popup, useMap, useMapEvents } from 'react-leaflet';
import { Link } from 'react-router-dom';
import { MapPin, School, Phone, Mail, Globe, Loader, Edit, X, Save, Trash2, Search } from 'lucide-react';
import Header from '../components/Header';
import { supabase, isSupabaseConfigured } from '../lib/supabase';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

interface SchoolData {
  id: number;
  school_type: string;
  X: number; // longitude (uppercase in database)
  Y: number; // latitude (uppercase in database)
  district_name: string;
  district_web_page: string;
  school_name: string;
  school_street_address: string;
  school_city: string;
  school_state: string;
  school_zip: string;
  school_phone: string;
  school_email_address: string;
  school_web_page: string;
  band_web_page: string;
  grade_range: string;
  school_Type: string;
  data_collected: boolean;
  signed_up: boolean;
}

interface DirectorData {
  id: number;
  school_id: number;
  school_name: string;
  dir_title: string | null;
  dir_prefix: string | null;
  dir_first: string | null;
  dir_last: string | null;
  dir_email: string | null;
  dir_notes: string | null;
  created_at: string;
  updated_at: string;
}

interface MapPosition {
  center: [number, number];
  zoom: number;
}

const MAP_STORAGE_KEY = 'bandcam_map_position';
const DEFAULT_MAP_POSITION: MapPosition = {
  center: [31.5, -99.5],
  zoom: 7
};

// Component to track and save map position to localStorage
const MapPositionTracker: React.FC = () => {
  const map = useMapEvents({
    moveend: () => {
      const center = map.getCenter();
      const zoom = map.getZoom();
      const position: MapPosition = {
        center: [center.lat, center.lng],
        zoom: zoom
      };
      localStorage.setItem(MAP_STORAGE_KEY, JSON.stringify(position));
    }
  });
  
  return null;
};

// Component to handle map bounds after data loads (only if no saved position)
const MapBounds: React.FC<{ schools: SchoolData[]; hasSavedPosition: boolean }> = ({ schools, hasSavedPosition }) => {
  const map = useMap();
  
  useEffect(() => {
    // Only auto-fit bounds if there's no saved position and we have schools
    if (!hasSavedPosition && schools.length > 0) {
      const validSchools = schools.filter(s => s.Y !== 0 && s.X !== 0);
      if (validSchools.length > 0) {
        const bounds = L.latLngBounds(
          validSchools.map(school => [school.Y, school.X])
        );
        map.fitBounds(bounds, { padding: [50, 50] });
      }
    }
  }, [schools, map, hasSavedPosition]);
  
  return null;
};

// Component to render circles with zoom-based sizing
const ZoomAwareCircleMarker: React.FC<{
  school: SchoolData;
  onSchoolSelect: (school: SchoolData) => void;
}> = ({ school, onSchoolSelect }) => {
  const map = useMap();
  const [zoom, setZoom] = useState(map.getZoom());
  
  useEffect(() => {
    const handleZoom = () => setZoom(map.getZoom());
    map.on('zoom', handleZoom);
    return () => {
      map.off('zoom', handleZoom);
    };
  }, [map]);
  
  // Calculate radius based on zoom level
  // Default zoom (7): radius = 2 (1/3 of original 6)
  // Zoom 11 (5 levels higher): radius = 6 (original size)
  // Linear interpolation between these points
  const minZoom = 7;
  const maxZoom = 11;
  const minRadius = 2;
  const maxRadius = 6;
  
  const radius = Math.max(
    minRadius,
    Math.min(
      maxRadius,
      minRadius + ((zoom - minZoom) / (maxZoom - minZoom)) * (maxRadius - minRadius)
    )
  );
  
  // Priority: signed_up (green) > data_collected (yellow) > default (red)
  const circleColor = school.signed_up ? '#10B981' : school.data_collected ? '#FFD700' : '#EF4444';
  
  return (
    <CircleMarker
      center={[school.Y, school.X]}
      radius={radius}
      pathOptions={{
        fillColor: circleColor,
        fillOpacity: 0.8,
        color: '#000000',
        weight: 1,
      }}
      eventHandlers={{
        click: () => onSchoolSelect(school),
      }}
    >
      <Popup>
        <div className="text-sm">
          <h3 className="font-bold mb-1">{school.school_name}</h3>
          <p className="text-gray-600">{school.school_Type}</p>
          <p className="text-gray-600">{school.school_city}, {school.school_state}</p>
          <p className="text-xs text-gray-500 mt-1">
            {school.signed_up ? '✓ Signed up' : school.data_collected ? '✓ Data collected' : '○ Not contacted'}
          </p>
        </div>
      </Popup>
    </CircleMarker>
  );
};

const SchoolsMapPage: React.FC = () => {
  const [schools, setSchools] = useState<SchoolData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedSchool, setSelectedSchool] = useState<SchoolData | null>(null);
  const [directors, setDirectors] = useState<DirectorData[]>([]);
  const [directorsLoading, setDirectorsLoading] = useState(false);
  const [filterType, setFilterType] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingSchool, setEditingSchool] = useState<SchoolData | null>(null);
  const [editFormData, setEditFormData] = useState<Partial<SchoolData>>({});
  const [isSaving, setIsSaving] = useState(false);
  
  // Director management state
  const [isDirectorModalOpen, setIsDirectorModalOpen] = useState(false);
  const [editingDirector, setEditingDirector] = useState<DirectorData | null>(null);
  const [directorFormData, setDirectorFormData] = useState<Partial<DirectorData>>({});
  const [isDirectorSaving, setIsDirectorSaving] = useState(false);
  
  // Map position state
  const [mapPosition] = useState<MapPosition>(() => {
    // Load saved position from localStorage on mount
    try {
      const saved = localStorage.getItem(MAP_STORAGE_KEY);
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (error) {
      console.error('Error loading saved map position:', error);
    }
    return DEFAULT_MAP_POSITION;
  });
  const [hasSavedPosition] = useState<boolean>(() => {
    return !!localStorage.getItem(MAP_STORAGE_KEY);
  });

  useEffect(() => {
    fetchSchools();
  }, []);

  const fetchDirectors = async (schoolId: number) => {
    if (!isSupabaseConfigured) return;
    
    setDirectorsLoading(true);
    try {
      const { data, error } = await supabase
        .from('directors')
        .select('*')
        .eq('school_id', schoolId)
        .order('dir_last, dir_first');

      if (error) throw error;
      
      setDirectors(data || []);
    } catch (err) {
      console.error('Error fetching directors:', err);
      setDirectors([]);
    } finally {
      setDirectorsLoading(false);
    }
  };

  const openEditModal = (school: SchoolData) => {
    setEditingSchool(school);
    setEditFormData({
      school_name: school.school_name || '',
      district_name: school.district_name || '',
      school_email_address: school.school_email_address || '',
      school_web_page: school.school_web_page || '',
      district_web_page: school.district_web_page || '',
      band_web_page: school.band_web_page || '',
      data_collected: school.data_collected || false,
      signed_up: school.signed_up || false
    });
    setIsEditModalOpen(true);
  };

  const closeEditModal = () => {
    setIsEditModalOpen(false);
    setEditingSchool(null);
    setEditFormData({});
  };

  const handleEditFormChange = (field: keyof SchoolData, value: any) => {
    setEditFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const saveSchool = async () => {
    if (!editingSchool || !isSupabaseConfigured) return;
    
    setIsSaving(true);
    try {
      // Use the unique ID to identify the record (more reliable than coordinates)
      const { error } = await supabase
        .from('schools')
        .update(editFormData)
        .eq('id', editingSchool.id)
        .select();

      if (error) throw error;

      // Update local state
      setSchools(prev => prev.map(school => 
        school.id === editingSchool.id
          ? { ...school, ...editFormData }
          : school
      ));

      // Update selected school if it's the same one
      if (selectedSchool?.id === editingSchool.id) {
        setSelectedSchool({ ...selectedSchool, ...editFormData });
      }

      closeEditModal();
    } catch (err) {
      console.error('Error saving school:', err);
      alert('Failed to save changes. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const deleteSchool = async () => {
    if (!editingSchool || !isSupabaseConfigured) return;
    
    if (!window.confirm(`Are you sure you want to delete "${editingSchool.school_name}"? This action cannot be undone.`)) {
      return;
    }

    setIsSaving(true);
    try {
      // Use the unique ID to identify the record
      const { error } = await supabase
        .from('schools')
        .delete()
        .eq('id', editingSchool.id);

      if (error) throw error;

      // Remove from local state
      setSchools(prev => prev.filter(school => school.id !== editingSchool.id));

      // Clear selected school if it's the deleted one
      if (selectedSchool?.id === editingSchool.id) {
        setSelectedSchool(null);
      }

      closeEditModal();
    } catch (err) {
      console.error('Error deleting school:', err);
      alert('Failed to delete school. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // Director management functions
  const openDirectorModal = (director?: DirectorData) => {
    if (director) {
      // Editing existing director
      setEditingDirector(director);
      setDirectorFormData({
        dir_title: director.dir_title || '',
        dir_prefix: director.dir_prefix || '',
        dir_first: director.dir_first || '',
        dir_last: director.dir_last || '',
        dir_email: director.dir_email || '',
        dir_notes: director.dir_notes || ''
      });
    } else {
      // Adding new director
      setEditingDirector(null);
      setDirectorFormData({
        dir_title: '',
        dir_prefix: '',
        dir_first: '',
        dir_last: '',
        dir_email: '',
        dir_notes: ''
      });
    }
    setIsDirectorModalOpen(true);
  };

  const closeDirectorModal = () => {
    setIsDirectorModalOpen(false);
    setEditingDirector(null);
    setDirectorFormData({});
  };

  const handleDirectorFormChange = (field: keyof DirectorData, value: any) => {
    setDirectorFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const saveDirector = async () => {
    if (!selectedSchool || !isSupabaseConfigured) return;
    
    setIsDirectorSaving(true);
    try {
      const directorData = {
        school_id: selectedSchool.id,
        school_name: selectedSchool.school_name,
        dir_title: directorFormData.dir_title || null,
        dir_prefix: directorFormData.dir_prefix || null,
        dir_first: directorFormData.dir_first || null,
        dir_last: directorFormData.dir_last || null,
        dir_email: directorFormData.dir_email || null,
        dir_notes: directorFormData.dir_notes || null
      };

      if (editingDirector) {
        // Update existing director
        const { error } = await supabase
          .from('directors')
          .update(directorData)
          .eq('id', editingDirector.id)
          .select();

        if (error) throw error;

        // Update local state
        setDirectors(prev => prev.map(dir => 
          dir.id === editingDirector.id
            ? { ...dir, ...directorData }
            : dir
        ));
      } else {
        // Add new director
        const { data, error } = await supabase
          .from('directors')
          .insert([directorData])
          .select();

        if (error) throw error;

        // Add to local state
        if (data && data.length > 0) {
          setDirectors(prev => [...prev, data[0]]);
        }
      }

      closeDirectorModal();
    } catch (err) {
      console.error('Error saving director:', err);
      alert('Failed to save director. Please try again.');
    } finally {
      setIsDirectorSaving(false);
    }
  };

  const deleteDirector = async (director: DirectorData) => {
    if (!isSupabaseConfigured) return;
    
    if (!window.confirm(`Are you sure you want to delete ${director.dir_first} ${director.dir_last}? This action cannot be undone.`)) {
      return;
    }

    setIsDirectorSaving(true);
    try {
      const { error } = await supabase
        .from('directors')
        .delete()
        .eq('id', director.id);

      if (error) throw error;

      // Remove from local state
      setDirectors(prev => prev.filter(dir => dir.id !== director.id));
    } catch (err) {
      console.error('Error deleting director:', err);
      alert('Failed to delete director. Please try again.');
    } finally {
      setIsDirectorSaving(false);
    }
  };

  const fetchSchools = async () => {
    try {
      setLoading(true);
      setError(null);

      // Check if Supabase is configured
      if (!isSupabaseConfigured) {
        throw new Error('Supabase is not configured. Please set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY environment variables in your deployment settings.');
      }

      console.log('Fetching schools from Supabase...');
      
      // Fetch all schools using pagination
      let allSchools: SchoolData[] = [];
      let from = 0;
      const pageSize = 1000;
      let hasMore = true;

      while (hasMore) {
        const { data, error: supabaseError } = await supabase
          .from('schools')
          .select('*')
          .order('school_name')
          .range(from, from + pageSize - 1);

        if (supabaseError) {
          throw supabaseError;
        }

        if (data && data.length > 0) {
          allSchools = [...allSchools, ...data];
          from += pageSize;
          hasMore = data.length === pageSize; // Continue if we got a full page
          console.log(`Fetched ${allSchools.length} schools so far...`);
        } else {
          hasMore = false;
        }
      }

      console.log('Total schools fetched:', allSchools.length);
      
      // Debug: Check what the data actually looks like
      if (allSchools.length > 0) {
        console.log('Sample raw school data:', allSchools[0]);
      }

      // Filter out schools with invalid coordinates
      // Must have valid lat/lng and not be 0,0
      // Note: Database uses uppercase X and Y
      const validSchools = allSchools.filter(
        (school: SchoolData) => {
          const lng = typeof school.X === 'string' ? parseFloat(school.X) : school.X;
          const lat = typeof school.Y === 'string' ? parseFloat(school.Y) : school.Y;
          
          return (
            lng != null && 
            lat != null && 
            !isNaN(lng) && 
            !isNaN(lat) &&
            lng !== 0 && 
            lat !== 0 &&
            lat >= -90 && 
            lat <= 90 &&
            lng >= -180 && 
            lng <= 180
          );
        }
      );

      console.log('Schools with valid coordinates:', validSchools.length);
      if (validSchools.length > 0) {
        console.log('Sample school:', validSchools[0]);
      }

      setSchools(validSchools);
    } catch (err) {
      console.error('Error fetching schools:', err);
      setError(err instanceof Error ? err.message : 'Failed to load schools data');
    } finally {
      setLoading(false);
    }
  };

  // Apply both type filter and search filter
  const filteredSchools = schools.filter(school => {
    // Type filter
    const matchesType = filterType === 'all' || 
      school.school_Type.toLowerCase().includes(filterType.toLowerCase());
    
    // Search filter
    const matchesSearch = searchQuery.trim() === '' || 
      school.school_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      school.school_city.toLowerCase().includes(searchQuery.toLowerCase()) ||
      school.district_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      school.school_Type.toLowerCase().includes(searchQuery.toLowerCase());
    
    return matchesType && matchesSearch;
  });

  const schoolTypes = [
    { value: 'all', label: 'All Schools', count: schools.length },
    { value: 'high', label: 'High Schools', count: schools.filter(s => s.school_Type.toLowerCase().includes('high')).length },
    { value: 'middle', label: 'Middle Schools', count: schools.filter(s => s.school_Type.toLowerCase().includes('middle')).length },
    { value: 'elementary', label: 'Elementary Schools', count: schools.filter(s => s.school_Type.toLowerCase().includes('elementary')).length },
  ];

  const resetMapView = () => {
    localStorage.removeItem(MAP_STORAGE_KEY);
    window.location.reload(); // Reload to apply default position
  };

  return (
    <div className="min-h-screen bg-midnight">
      {/* Header */}
      <Header />

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-white mb-4">
            Texas Schools Map
          </h1>
          <p className="text-lg text-gray-300 mb-4">
            Explore potential partner schools across Texas
          </p>
        </div>

        {/* Filters */}
        <div className="bg-sky border-2 border-black p-4 mb-6">
          {/* Search Bar */}
          <div className="mb-4">
            <div className="relative max-w-2xl">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" />
              <input
                type="text"
                placeholder="Search by school name, city, or district..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border-2 border-black rounded-lg text-black placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-goldie"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-black"
                  title="Clear search"
                >
                  <X className="w-5 h-5" />
                </button>
              )}
            </div>
            {searchQuery && (
              <p className="text-sm text-black mt-2">
                Showing {filteredSchools.length} of {schools.length} schools
              </p>
            )}
          </div>

          {/* Type Filters */}
          <div className="flex flex-wrap items-center gap-2">
            {schoolTypes.map(type => (
              <button
                key={type.value}
                onClick={() => setFilterType(type.value)}
                className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                  filterType === type.value
                    ? 'bg-goldie text-black'
                    : 'bg-white text-black hover:bg-gray-100'
                }`}
              >
                {type.label} ({type.count})
              </button>
            ))}
            <div className="ml-auto">
              <button
                onClick={resetMapView}
                className="px-4 py-2 rounded-lg font-semibold transition-colors bg-gray-200 text-black hover:bg-gray-300 flex items-center gap-2"
                title="Reset map to default view (shows all schools)"
              >
                <MapPin className="w-4 h-4" />
                Reset Map View
              </button>
            </div>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="bg-sky border-2 border-black p-12 text-center">
            <Loader className="w-12 h-12 animate-spin text-black mx-auto mb-4" />
            <p className="text-lg text-black">Loading schools data...</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="bg-red-100 border-2 border-red-500 p-6">
            <h3 className="text-lg font-bold text-red-700 mb-4">Unable to Load Schools Map</h3>
            <p className="text-red-700 mb-4">{error}</p>
            
            {!isSupabaseConfigured && (
              <div className="bg-red-50 border border-red-300 p-4 mb-4 text-left">
                <p className="font-semibold text-red-900 mb-2">Configuration Issue:</p>
                <p className="text-sm text-red-800 mb-2">
                  The Supabase environment variables are not configured for this deployment.
                </p>
                <p className="text-sm text-red-800 mb-2">
                  Required environment variables:
                </p>
                <ul className="list-disc list-inside text-sm text-red-800 mb-2">
                  <li><code>REACT_APP_SUPABASE_URL</code></li>
                  <li><code>REACT_APP_SUPABASE_ANON_KEY</code></li>
                </ul>
                <p className="text-sm text-red-800">
                  If you're the site administrator, please add these environment variables in your Vercel project settings and redeploy.
                </p>
              </div>
            )}
            
            <div className="flex gap-4">
              <button
                onClick={fetchSchools}
                className="bg-red-500 text-white px-6 py-2 rounded-lg font-semibold hover:bg-red-600"
              >
                Try Again
              </button>
            </div>
          </div>
        )}

        {/* Map Container */}
        {!loading && !error && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Map */}
            <div className="lg:col-span-2">
              <div className="bg-sky border-2 border-black overflow-hidden" style={{ height: '600px' }}>
                <MapContainer
                  center={mapPosition.center}
                  zoom={mapPosition.zoom}
                  style={{ height: '100%', width: '100%' }}
                  className="z-0"
                >
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  <MapPositionTracker />
                  <MapBounds schools={filteredSchools} hasSavedPosition={hasSavedPosition} />
                  {filteredSchools.map((school) => (
                    <ZoomAwareCircleMarker
                      key={school.id}
                      school={school}
                      onSchoolSelect={(school) => {
                        setSelectedSchool(school);
                        fetchDirectors(school.id);
                      }}
                    />
                  ))}
                </MapContainer>
              </div>
              
              {/* Legend */}
              <div className="bg-sky border-2 border-black border-t-0 p-4">
                <h3 className="font-semibold text-black mb-2">Legend</h3>
                <div className="flex flex-wrap gap-4">
                  <div className="flex items-center">
                    <div className="w-4 h-4 rounded-full mr-2 border border-black" style={{backgroundColor: '#EF4444'}}></div>
                    <span className="text-sm text-black">
                      Not Contacted ({filteredSchools.filter(s => !s.signed_up && !s.data_collected).length})
                    </span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-4 h-4 rounded-full mr-2 border border-black" style={{backgroundColor: '#FFD700'}}></div>
                    <span className="text-sm text-black">
                      Data Collected ({filteredSchools.filter(s => s.data_collected && !s.signed_up).length})
                    </span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-4 h-4 rounded-full mr-2 border border-black" style={{backgroundColor: '#10B981'}}></div>
                    <span className="text-sm text-black">
                      Signed Up ({filteredSchools.filter(s => s.signed_up).length})
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* School Details Panel */}
            <div className="lg:col-span-1">
              <div className="bg-sky border-2 border-black p-6 sticky top-4">
                {selectedSchool ? (
                  <div>
                    <div className="flex items-start justify-between mb-4">
                      <h3 className="text-xl font-bold text-black">
                        {selectedSchool.school_name}
                      </h3>
                      <button
                        onClick={() => {
                          setSelectedSchool(null);
                          setDirectors([]);
                        }}
                        className="text-black hover:text-gray-700"
                      >
                        ×
                      </button>
                    </div>

                    <div className="space-y-3 text-sm">
                      <div className="flex items-start">
                        <School className="w-4 h-4 text-black mr-2 mt-1 flex-shrink-0" />
                        <div>
                          <p className="font-semibold text-black">Type</p>
                          <p className="text-gray-700">{selectedSchool.school_Type}</p>
                        </div>
                      </div>

                      <div className="flex items-start">
                        <MapPin className="w-4 h-4 text-black mr-2 mt-1 flex-shrink-0" />
                        <div>
                          <p className="font-semibold text-black">Address</p>
                          <p className="text-gray-700">
                            {selectedSchool.school_street_address}<br />
                            {selectedSchool.school_city}, {selectedSchool.school_state} {selectedSchool.school_zip}
                          </p>
                        </div>
                      </div>

                      {selectedSchool.school_phone && (
                        <div className="flex items-start">
                          <Phone className="w-4 h-4 text-black mr-2 mt-1 flex-shrink-0" />
                          <div>
                            <p className="font-semibold text-black">Phone</p>
                            <a 
                              href={`tel:${selectedSchool.school_phone}`}
                              className="text-goldie hover:underline font-semibold"
                            >
                              {selectedSchool.school_phone}
                            </a>
                          </div>
                        </div>
                      )}

                      {selectedSchool.school_email_address && (
                        <div className="flex items-start">
                          <Mail className="w-4 h-4 text-black mr-2 mt-1 flex-shrink-0" />
                          <div>
                            <p className="font-semibold text-black">Email</p>
                            <a 
                              href={`mailto:${selectedSchool.school_email_address}`}
                              className="text-goldie hover:underline break-all font-semibold"
                            >
                              {selectedSchool.school_email_address}
                            </a>
                          </div>
                        </div>
                      )}

                      {selectedSchool.school_web_page && (
                        <div className="flex items-start">
                          <Globe className="w-4 h-4 text-black mr-2 mt-1 flex-shrink-0" />
                          <div>
                            <p className="font-semibold text-black">Website</p>
                            <a 
                              href={selectedSchool.school_web_page.startsWith('http') 
                                ? selectedSchool.school_web_page 
                                : `https://${selectedSchool.school_web_page}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-goldie hover:underline break-all font-semibold"
                            >
                              {selectedSchool.school_web_page}
                            </a>
                          </div>
                        </div>
                      )}

                      <div className="pt-3 border-t border-gray-300">
                        <p className="font-semibold text-black mb-1">District</p>
                        <p className="text-gray-700">{selectedSchool.district_name}</p>
                        {selectedSchool.district_web_page && (
                          <a 
                            href={selectedSchool.district_web_page.startsWith('http') 
                              ? selectedSchool.district_web_page 
                              : `https://${selectedSchool.district_web_page}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-goldie hover:underline text-sm break-all font-semibold"
                          >
                            {selectedSchool.district_web_page}
                          </a>
                        )}
                      </div>

                      {selectedSchool.grade_range && (
                        <div>
                          <p className="font-semibold text-black">Grades</p>
                          <p className="text-gray-700">{selectedSchool.grade_range}</p>
                        </div>
                      )}
                    </div>

                    {/* Directors Section */}
                    <div className="pt-4 border-t border-gray-300">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-semibold text-black">Band Directors</h4>
                        <button
                          onClick={() => openDirectorModal()}
                          className="flex items-center gap-1 bg-green-500 text-white px-3 py-1 rounded text-sm font-semibold hover:bg-green-600 transition-colors"
                        >
                          <span>+</span>
                          Add Director
                        </button>
                      </div>
                      
                      {directorsLoading ? (
                        <div className="flex items-center">
                          <Loader className="w-4 h-4 animate-spin text-black mr-2" />
                          <p className="text-sm text-gray-600">Loading directors...</p>
                        </div>
                      ) : directors.length > 0 ? (
                        <div className="space-y-3">
                          {directors.map((director) => (
                            <div key={director.id} className="bg-gray-50 p-3 rounded-lg">
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <h5 className="font-semibold text-black">
                                    {director.dir_prefix && `${director.dir_prefix} `}
                                    {director.dir_first} {director.dir_last}
                                  </h5>
                                  {director.dir_title && (
                                    <p className="text-sm text-gray-600 mb-1">{director.dir_title}</p>
                                  )}
                                  {director.dir_email && (
                                    <a 
                                      href={`mailto:${director.dir_email}`}
                                      className="text-goldie hover:underline text-sm break-all font-semibold"
                                    >
                                      {director.dir_email}
                                    </a>
                                  )}
                                  {director.dir_notes && (
                                    <p className="text-xs text-gray-500 mt-1">{director.dir_notes}</p>
                                  )}
                                </div>
                                <div className="flex gap-1 ml-2">
                                  <button
                                    onClick={() => openDirectorModal(director)}
                                    className="text-blue-600 hover:text-blue-800 text-sm"
                                    title="Edit Director"
                                  >
                                    <Edit className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={() => deleteDirector(director)}
                                    className="text-red-600 hover:text-red-800 text-sm"
                                    title="Delete Director"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-4">
                          <p className="text-sm text-gray-600 mb-3">No directors found for this school.</p>
                          <button
                            onClick={() => openDirectorModal()}
                            className="bg-green-500 text-white px-4 py-2 rounded-lg font-semibold hover:bg-green-600 transition-colors"
                          >
                            Add First Director
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Edit Button */}
                    <div className="mt-4 pt-4 border-t border-gray-300">
                      <button
                        onClick={() => openEditModal(selectedSchool)}
                        className="w-full flex items-center justify-center gap-2 bg-blue-500 text-white px-4 py-2 rounded-lg font-semibold hover:bg-blue-600 transition-colors"
                      >
                        <Edit className="w-4 h-4" />
                        Edit School
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="text-center text-gray-600">
                    <MapPin className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-lg font-semibold text-black mb-2">
                      Select a School
                    </p>
                    <p className="text-sm text-gray-700">
                      Click on any marker on the map to view school details
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Stats */}
        {!loading && !error && (
          <div className="mt-6 bg-sky border-2 border-black p-6">
            <h3 className="text-xl font-bold text-black mb-4">Statistics</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center">
                <p className="text-3xl font-bold text-black">{schools.length}</p>
                <p className="text-sm text-gray-700">Total Schools</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-black">
                  {schools.filter(s => s.school_Type.toLowerCase().includes('high')).length}
                </p>
                <p className="text-sm text-gray-700">High Schools</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-black">
                  {schools.filter(s => s.school_Type.toLowerCase().includes('middle')).length}
                </p>
                <p className="text-sm text-gray-700">Middle Schools</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-black">
                  {new Set(schools.map(s => s.district_name)).size}
                </p>
                <p className="text-sm text-gray-700">Districts</p>
              </div>
            </div>
          </div>
        )}

        {/* Edit Modal */}
        {isEditModalOpen && editingSchool && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold text-black">Edit School</h2>
                  <button
                    onClick={closeEditModal}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                <div className="grid grid-cols-1 gap-4">
                  {/* Basic Information */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">School Name</label>
                    <input
                      type="text"
                      value={editFormData.school_name || ''}
                      onChange={(e) => handleEditFormChange('school_name', e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-black"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">District Name</label>
                    <input
                      type="text"
                      value={editFormData.district_name || ''}
                      onChange={(e) => handleEditFormChange('district_name', e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-black"
                    />
                  </div>

                  {/* Contact Information */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    <input
                      type="email"
                      value={editFormData.school_email_address || ''}
                      onChange={(e) => handleEditFormChange('school_email_address', e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-black"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">School Website</label>
                    <input
                      type="url"
                      value={editFormData.school_web_page || ''}
                      onChange={(e) => handleEditFormChange('school_web_page', e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-black"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">District Website</label>
                    <input
                      type="url"
                      value={editFormData.district_web_page || ''}
                      onChange={(e) => handleEditFormChange('district_web_page', e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-black"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Band Website</label>
                    <input
                      type="url"
                      value={editFormData.band_web_page || ''}
                      onChange={(e) => handleEditFormChange('band_web_page', e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-black"
                    />
                  </div>


                  {/* Status */}
                  <div className="space-y-3">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={editFormData.data_collected || false}
                        onChange={(e) => handleEditFormChange('data_collected', e.target.checked)}
                        className="rounded"
                      />
                      <span className="text-sm font-medium text-gray-700">Data Collected</span>
                    </label>
                    
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={editFormData.signed_up || false}
                        onChange={(e) => handleEditFormChange('signed_up', e.target.checked)}
                        className="rounded"
                      />
                      <span className="text-sm font-medium text-gray-700">Signed Up</span>
                    </label>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-4 mt-6 pt-6 border-t border-gray-300">
                  <button
                    onClick={saveSchool}
                    disabled={isSaving}
                    className="flex items-center gap-2 bg-green-500 text-white px-6 py-2 rounded-lg font-semibold hover:bg-green-600 disabled:opacity-50"
                  >
                    <Save className="w-4 h-4" />
                    {isSaving ? 'Saving...' : 'Save Changes'}
                  </button>
                  
                  <button
                    onClick={deleteSchool}
                    disabled={isSaving}
                    className="flex items-center gap-2 bg-red-500 text-white px-6 py-2 rounded-lg font-semibold hover:bg-red-600 disabled:opacity-50"
                  >
                    <Trash2 className="w-4 h-4" />
                    Delete School
                  </button>
                  
                  <button
                    onClick={closeEditModal}
                    disabled={isSaving}
                    className="flex items-center gap-2 bg-gray-500 text-white px-6 py-2 rounded-lg font-semibold hover:bg-gray-600 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Director Modal */}
        {isDirectorModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-lg w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold text-black">
                    {editingDirector ? 'Edit Director' : 'Add Director'}
                  </h2>
                  <button
                    onClick={closeDirectorModal}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                <div className="space-y-4">
                  {/* School Info (read-only) */}
                  {selectedSchool && (
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-sm font-medium text-gray-700 mb-1">School</p>
                      <p className="text-black">{selectedSchool.school_name}</p>
                    </div>
                  )}

                  {/* Prefix */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Prefix</label>
                    <select
                      value={directorFormData.dir_prefix || ''}
                      onChange={(e) => handleDirectorFormChange('dir_prefix', e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-black"
                    >
                      <option value="">Select...</option>
                      <option value="Mr.">Mr.</option>
                      <option value="Mrs.">Mrs.</option>
                      <option value="Ms.">Ms.</option>
                      <option value="Dr.">Dr.</option>
                    </select>
                  </div>

                  {/* First Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                    <input
                      type="text"
                      value={directorFormData.dir_first || ''}
                      onChange={(e) => handleDirectorFormChange('dir_first', e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-black"
                      required
                    />
                  </div>

                  {/* Last Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
                    <input
                      type="text"
                      value={directorFormData.dir_last || ''}
                      onChange={(e) => handleDirectorFormChange('dir_last', e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-black"
                      required
                    />
                  </div>

                  {/* Title */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                    <input
                      type="text"
                      value={directorFormData.dir_title || ''}
                      onChange={(e) => handleDirectorFormChange('dir_title', e.target.value)}
                      placeholder="e.g., Director of Bands, Assistant Director"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-black"
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    <input
                      type="email"
                      value={directorFormData.dir_email || ''}
                      onChange={(e) => handleDirectorFormChange('dir_email', e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-black"
                    />
                  </div>

                  {/* Notes */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                    <textarea
                      value={directorFormData.dir_notes || ''}
                      onChange={(e) => handleDirectorFormChange('dir_notes', e.target.value)}
                      placeholder="Any additional information about this director..."
                      rows={3}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-black"
                    />
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-4 mt-6 pt-6 border-t border-gray-300">
                  <button
                    onClick={saveDirector}
                    disabled={isDirectorSaving || !directorFormData.dir_first || !directorFormData.dir_last}
                    className="flex items-center gap-2 bg-green-500 text-white px-6 py-2 rounded-lg font-semibold hover:bg-green-600 disabled:opacity-50"
                  >
                    <Save className="w-4 h-4" />
                    {isDirectorSaving ? 'Saving...' : (editingDirector ? 'Update Director' : 'Add Director')}
                  </button>
                  
                  <button
                    onClick={closeDirectorModal}
                    disabled={isDirectorSaving}
                    className="flex items-center gap-2 bg-gray-500 text-white px-6 py-2 rounded-lg font-semibold hover:bg-gray-600 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <footer className="bg-sky border-t-2 border-black mt-12 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-black">
            Data represents potential partner schools in Texas. For inquiries about your school, please{' '}
            <Link to="/contact" className="underline hover:text-gray-700">
              contact us
            </Link>
            .
          </p>
        </div>
      </footer>
    </div>
  );
};

export default SchoolsMapPage;

